// src/pages/ProductMatchCandidatesPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Pagination from "../components/Pagination";
import {
    getCandidates,
    approveCandidate,
    rejectCandidate,
    bulkUpdate,
    updateCandidate,
    type ProductMatchCandidate,
    type PMCStatus,
} from "../helpers/ProductMatchCandidates";
import StoreTag from "../components/StoreTag";

type Filters = {
    search?: string | null;        // по имени/SKU/ID (бэк можешь мапнуть на product_id/brand/bucket)
    brand?: string;
    status?: PMCStatus | "all";
    min_score?: number;
    max_score?: number;
    only_pending?: boolean;
    bucket?: string;
};

export default function ProductMatchCandidatesPage() {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<ProductMatchCandidate[]>([]);
    const [page, setPage] = useState<number>(1);
    const [perPage] = useState<number>(50);
    const [meta, setMeta] = useState<{ page: number; totalPages: number }>({
        page: 1,
        totalPages: 1,
    });

    const [filters, setFilters] = useState<Filters>({
        search: "",
        status: "all",
        min_score: 0.86,
        max_score: undefined,
        only_pending: true,
    });

    const abortRef = useRef<AbortController | null>(null);
    const [checked, setChecked] = useState<Record<number, boolean>>({}); // candidate.id -> checked

    // --- debounced search
    const searchRef = useRef<number>();
    const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (searchRef.current) window.clearTimeout(searchRef.current);
        searchRef.current = window.setTimeout(() => {
            setFilters((f) => ({ ...f, search: val }));
            if (page !== 1) setPage(1);
            else fetchData({ page: 1, filters: { ...filters, search: val } });
        }, 350);
    };

    const fetchData = useCallback(
        async ({
            page: p = page,
            filters: f = filters,
        }: { page?: number; filters?: Filters } = {}) => {
            try {
                setLoading(true);
                if (abortRef.current) abortRef.current.abort();
                abortRef.current = new AbortController();

                // Преобразование UI‑фильтров под API
                const apiFilters: any = {
                    brand: f.brand,
                    status: f.status && f.status !== "all" ? f.status : undefined,
                    min_score: f.min_score,
                    max_score: f.max_score,
                    bucket: f.bucket,
                    only_pending: f.only_pending ? 1 : undefined,
                    // опционально: product_id / search — на бэке разрули как нужно
                    search: f.search,
                };

                const res = await getCandidates(
                    { page: p, perPage, filters: apiFilters },
                    { signal: abortRef.current.signal }
                );

                setItems(res.data);
                setMeta({ page: res.current_page, totalPages: res.last_page });
            } catch (e) {
                if ((e as any)?.name !== "AbortError") {
                    console.error(e);
                }
            } finally {
                setLoading(false);
            }
        },
        [page, perPage, filters]
    );

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, perPage, filters.brand, filters.status, filters.min_score, filters.max_score, filters.only_pending, filters.bucket]);

    const allChecked = useMemo(
        () => items.length > 0 && items.every((i) => checked[i.id]),
        [items, checked]
    );

    const toggleAll = () => {
        const next: Record<number, boolean> = {};
        if (!allChecked) items.forEach((i) => (next[i.id] = true));
        setChecked(next);
    };

    const selectedIds = useMemo(
        () => Object.entries(checked).filter(([, v]) => v).map(([k]) => Number(k)),
        [checked]
    );

    const clearSelection = () => setChecked({});

    async function onApprove(id: number, masterId?: number) {
        // сохраняем старое состояние для возможного отката
        const prevItems = [...items];

        // оптимистично меняем статус
        setItems(items.map(item =>
            item.id === id
                ? { ...item, status: "approved", suggested_master_id: masterId ?? item.suggested_master_id }
                : item
        ));

        try {
            await approveCandidate(id, masterId ? { master_id: masterId } : {});
        } catch (err) {
            setItems(prevItems);
        }
    }

    async function onIgnore(id: number) {
        // сохраняем старое состояние для возможного отката
        const prevItems = [...items];

        // оптимистично меняем статус
        setItems(items.map(item =>
            item.id === id
                ? { ...item, status: "ignored" }
                : item
        ));

        try {
            await updateCandidate(id, { status: "ignored" });
        } catch (err) {
            setItems(prevItems);
        }
    }

    async function onReject(id: number) {
        // сохраняем старое состояние для возможного отката
        const prevItems = [...items];

        // оптимистично меняем статус
        setItems(items.map(item =>
            item.id === id
                ? { ...item, status: "rejected" }
                : item
        ));

        try {
            await rejectCandidate(id, {});
        } catch (err) {
            setItems(prevItems);
        }
    }
    async function onBulk(action: PMCStatus | "ignore" | "pending") {
        if (selectedIds.length === 0) return;
        await bulkUpdate(selectedIds, action);
        clearSelection();
        await fetchData();
    }

    async function onSetSuggested(id: number, suggested: number | null) {
        // сохраняем старое состояние для возможного отката
        const prevItems = [...items];

        // оптимистично меняем статус
        setItems(items.map(item =>
            item.id === id
                ? { ...item, suggested_master_id: suggested ? suggested : item.suggested_master_id }
                : item
        ));

        try {
            await updateCandidate(id, { suggested_master_id: suggested ?? null });
        } catch (err) {
            setItems(prevItems);
        }
    }

    const scoreClass = (s: number) =>
        s >= 0.95 ? "bg-green-100 text-green-800" :
            s >= 0.9 ? "bg-emerald-100 text-emerald-800" :
                s >= 0.86 ? "bg-yellow-100 text-yellow-800" :
                    "bg-gray-100 text-gray-700";

    return (
        <>
            <div className="flex justify-between items-start gap-4">
                <div className="space-y-2">
                    <h1 className="text-xl font-semibold">Product match candidates</h1>
                    <div className="flex flex-wrap gap-2 items-center">
                        <input
                            className="border px-2 py-1 rounded"
                            placeholder="Search (name, SKU, product id, bucket…) "
                            defaultValue={filters.search ?? ""}
                            onChange={onSearchChange}
                        />
                        <input
                            className="border px-2 py-1 rounded w-32"
                            placeholder="Brand"
                            value={filters.brand ?? ""}
                            onChange={(e) => {
                                setFilters((f) => ({ ...f, brand: e.target.value || undefined }));
                                if (page !== 1) setPage(1);
                            }}
                        />
                        <select
                            className="border px-2 py-1 rounded"
                            value={filters.status ?? "all"}
                            onChange={(e) => {
                                setFilters((f) => ({ ...f, status: e.target.value as any }));
                                if (page !== 1) setPage(1);
                            }}
                        >
                            <option value="all">All statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="rejected">Rejected</option>
                            <option value="ignored">Ignored</option>
                        </select>
                        <label className="flex items-center gap-1 text-sm">
                            <input
                                type="checkbox"
                                checked={!!filters.only_pending}
                                onChange={(e) => setFilters((f) => ({ ...f, only_pending: e.target.checked || undefined }))}
                            />
                            Only pending
                        </label>
                        <input
                            type="number"
                            step="0.01"
                            min={0}
                            max={1}
                            className="border px-2 py-1 rounded w-28"
                            placeholder="Min score"
                            value={filters.min_score ?? ""}
                            onChange={(e) =>
                                setFilters((f) => ({
                                    ...f,
                                    min_score: e.target.value === "" ? undefined : Number(e.target.value),
                                }))
                            }
                        />
                        <input
                            type="number"
                            step="0.01"
                            min={0}
                            max={1}
                            className="border px-2 py-1 rounded w-28"
                            placeholder="Max score"
                            value={filters.max_score ?? ""}
                            onChange={(e) =>
                                setFilters((f) => ({
                                    ...f,
                                    max_score: e.target.value === "" ? undefined : Number(e.target.value),
                                }))
                            }
                        />
                        <input
                            className="border px-2 py-1 rounded w-56"
                            placeholder="Bucket key"
                            value={filters.bucket ?? ""}
                            onChange={(e) => setFilters((f) => ({ ...f, bucket: e.target.value || undefined }))}
                        />
                        <button
                            className="px-3 py-1 rounded border"
                            onClick={() => {
                                setFilters({ search: "", status: "all", min_score: 0.86, only_pending: true });
                                if (page !== 1) setPage(1);
                                else fetchData({ page: 1, filters: { search: "", status: "all", min_score: 0.86, only_pending: true } });
                            }}
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <button
                        className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50"
                        disabled={selectedIds.length === 0}
                        onClick={() => onBulk("approved")}
                    >
                        Approve selected
                    </button>
                    <button
                        className="px-3 py-1 rounded bg-gray-800 text-white disabled:opacity-50"
                        disabled={selectedIds.length === 0}
                        onClick={() => onBulk("ignored")}
                    >
                        Ignore selected
                    </button>
                    <button
                        className="px-3 py-1 rounded bg-red-600 text-white disabled:opacity-50"
                        disabled={selectedIds.length === 0}
                        onClick={() => onBulk("rejected")}
                    >
                        Reject selected
                    </button>
                    {selectedIds.length > 0 && (
                        <button className="px-3 py-1 rounded border" onClick={clearSelection}>
                            Clear selection ({selectedIds.length})
                        </button>
                    )}
                </div>
            </div>

            <div className="mt-3 p-3 rounded bg-white">
                {loading ? (
                    <div className="grid min-h-[140px] w-full place-items-center p-6">
                        <svg className="text-blue-300 w-12 h-12 animate-spin" viewBox="0 0 64 64" fill="none">
                            <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="5" className="opacity-25" />
                            <path d="M32 4a28 28 0 0 1 28 28" stroke="currentColor" strokeWidth="5" className="opacity-90" />
                        </svg>
                    </div>
                ) : items.length === 0 ? (
                    <div className="p-6 text-gray-600">Ничего не нашлось.</div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="text-left">
                            <tr className="border-b">
                                <th className="p-2 w-8">
                                    <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                                </th>
                                <th className="p-2">Score</th>
                                <th className="p-2">A (candidate)</th>
                                <th className="p-2">B (candidate)</th>
                                <th className="p-2">Brand / Bucket</th>
                                <th className="p-2">Suggested</th>
                                <th className="p-2">Status</th>
                                <th className="p-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => {
                                const s = Number(c.score || 0);
                                const suggested = c.suggested_master_id ?? undefined;

                                console.log(c)
                                return (
                                    <tr key={c.id} className="border-b last:border-0 align-top">
                                        <td className="p-2">
                                            <input
                                                type="checkbox"
                                                checked={!!checked[c.id]}
                                                onChange={(e) => setChecked((m) => ({ ...m, [c.id]: e.target.checked }))}
                                            />
                                        </td>
                                        <td className="p-2">
                                            <span className={`px-2 py-0.5 rounded ${scoreClass(s)}`}>{s.toFixed(3)}</span>
                                        </td>

                                        <td className="p-2">
                                            <ProdCell product={c['product_a']} />
                                        </td>
                                        <td className="p-2">
                                            <ProdCell product={c['product_b']} />
                                        </td>

                                        <td className="p-2">
                                            <div className="text-xs text-gray-700">
                                                {c.brand || "—"}
                                                <div className="text-gray-500">{c.bucket_key || "—"}</div>
                                            </div>
                                        </td>

                                        <td className="p-2">
                                            <div className="flex items-center gap-2">
                                                <select
                                                    className="border rounded px-1 py-0.5"
                                                    value={suggested ?? ""}
                                                    onChange={(e) =>
                                                        onSetSuggested(c.id, e.target.value ? Number(e.target.value) : null)
                                                    }
                                                >
                                                    <option value="">—</option>
                                                    {c['product_a'] && <option value={c.product_a_id}>A: {c.product_a_id}</option>}
                                                    {c['product_b'] && <option value={c.product_b_id}>B: {c.product_b_id}</option>}
                                                </select>
                                                {suggested && (
                                                    <Link
                                                        to={`/products/${suggested}`}
                                                        className="text-blue-600 underline"
                                                    >
                                                        open
                                                    </Link>
                                                )}
                                            </div>
                                        </td>

                                        <td className="p-2">
                                            <StatusBadge status={(c.status as PMCStatus) || "pending"} />
                                        </td>

                                        <td className="p-2">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    className="px-2 py-1 rounded bg-blue-600 text-white"
                                                    onClick={() => onApprove(c.id, suggested)}
                                                    title="Approve (merge later вручную)"
                                                >
                                                    Approve
                                                </button>
                                                <button
                                                    className="px-2 py-1 rounded bg-gray-800 text-white"
                                                    onClick={() => onIgnore(c.id, suggested)}
                                                >
                                                    Ignore
                                                </button>
                                                <button
                                                    className="px-2 py-1 rounded bg-red-600 text-white"
                                                    onClick={() => onReject(c.id)}
                                                >
                                                    Reject
                                                </button>
                                            </div>
                                            <div className="mt-1 text-right text-xs text-gray-500">
                                                id:{c.id}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {!loading && items.length > 0 && (
                <div className="w-full mt-5">
                    <Pagination page={meta.page} totalPages={meta.totalPages} onCallback={setPage} />
                </div>
            )}
        </>
    );
}

function ProdCell({ product }: { product?: { id: number; name: string; sku: string, alias: string, store_products: any[] } | undefined }) {
    console.log(product)
    if (!product) return <div className="text-gray-500">—</div>;
    return (
        <div className="max-w-[420px]">
            <div className="font-medium leading-snug">
                <Link to={`/products/${product.id}`} className="text-blue-600 underline">
                    {product.name}
                </Link>
            </div>
            <div className="text-xs text-gray-600 mt-0.5">
                id:{product.id} • SKU: {product.sku || "—"}
            </div>
            <div className="text-xs text-gray-600 mt-0.5 flex gap-x-2">
                {product['store_products'].map((storeProduct) => (
                    <StoreTag name={storeProduct.store.name} marketplace={storeProduct.marketplace} />
                ))}
            </div>
            <div className="text-xs mt-1">
                <a
                    className="text-blue-500 underline"
                    href={`https://pricehunt.ca/products/${product.alias}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    Public page
                </a>
            </div>
        </div>
    );
}

function StatusBadge({ status }: { status: PMCStatus }) {
    const map: Record<PMCStatus, string> = {
        pending: "bg-yellow-100 text-yellow-800",
        approved: "bg-green-100 text-green-800",
        rejected: "bg-red-100 text-red-800",
        ignored: "bg-gray-100 text-gray-800",
    };
    return <span className={`px-2 py-0.5 rounded text-xs ${map[status]}`}>{status}</span>;
}